"use client";

import { BadgeCheck, CalendarCheck, CalendarX, UserMinus, Zap } from "lucide-react";
import { type Contact, Conversation, type ThreadItem } from "../_shared/conversation";
import {
  FeatureUseCases,
  type SceneProps,
  type UseCase,
  useRevealCount,
} from "../_shared/use-cases-section";

/* ------------------------------------------------------------------ */
/* 1 · New-lead nurture — a first class becomes a membership           */
/* ------------------------------------------------------------------ */

const DEVIN: Contact = {
  name: "Devin Carter",
  src: "/images/website/customers/devin-carter.jpg",
  status: "New lead",
};

const NEW_LEAD: ThreadItem[] = [
  {
    kind: "event",
    id: "n1",
    icon: Zap,
    title: "Enrolled · New-lead nurture",
    sub: "Booked a first class",
  },
  {
    kind: "message",
    id: "n2",
    direction: "outgoing",
    body: "Hi Devin! You're booked for Saturday 9am 🎉 We've saved your spot — reply here anytime.",
  },
  { kind: "system", id: "n3", text: "Waited 1 day" },
  {
    kind: "event",
    id: "n4",
    icon: CalendarCheck,
    title: "Attended first class",
    sub: "Sat · 9:00 AM",
  },
  { kind: "system", id: "n5", text: "Waited 2 days" },
  {
    kind: "email",
    id: "n6",
    direction: "outgoing",
    subject: "Your next month, on us",
    body: "Loved having you Saturday! Here's 20% off your first month of membership.",
  },
  {
    kind: "message",
    id: "n7",
    direction: "incoming",
    body: "That class was so good. Okay, I'm in!",
  },
  {
    kind: "event",
    id: "n8",
    icon: BadgeCheck,
    title: "Joined membership",
    sub: "Exited flow · converted",
  },
];

function NewLeadScene({ progress, reduce, flow, active }: SceneProps) {
  const visible = useRevealCount(progress, NEW_LEAD.length, reduce, active);
  return (
    <Conversation contact={DEVIN} items={NEW_LEAD} visible={visible} reduce={reduce} flow={flow} />
  );
}

/* ------------------------------------------------------------------ */
/* 2 · No-show recovery — save the visit before it slips              */
/* ------------------------------------------------------------------ */

const CASEY: Contact = {
  name: "Casey Lin",
  src: "/images/website/customers/casey-lin.jpg",
  status: "Member",
  statusTone: "brand",
};

const NO_SHOW: ThreadItem[] = [
  { kind: "event", id: "s1", icon: CalendarX, title: "No-showed · 6pm HIIT", sub: "Today" },
  { kind: "system", id: "s2", text: "Triggered automatically" },
  {
    kind: "message",
    id: "s3",
    direction: "outgoing",
    body: "Hey Casey, sorry we missed you at 6pm HIIT! Want me to grab you a spot tomorrow, same time?",
  },
  {
    kind: "message",
    id: "s4",
    direction: "incoming",
    body: "Ugh, work ran late. Yes please 🙏",
  },
  {
    kind: "message",
    id: "s5",
    direction: "outgoing",
    body: "Done — you're in for Thursday 6pm. See you there!",
  },
  {
    kind: "event",
    id: "s6",
    icon: CalendarCheck,
    title: "Rebooked",
    sub: "Exited flow · Thu 6:00 PM",
  },
];

function NoShowScene({ progress, reduce, flow, active }: SceneProps) {
  const visible = useRevealCount(progress, NO_SHOW.length, reduce, active);
  return (
    <Conversation contact={CASEY} items={NO_SHOW} visible={visible} reduce={reduce} flow={flow} />
  );
}

/* ------------------------------------------------------------------ */
/* 3 · Win-back — bring a lapsed member back through the door          */
/* ------------------------------------------------------------------ */

const MARISSA: Contact = {
  name: "Marissa Larimer",
  src: "/images/website/customers/marissa-larimer.jpg",
  status: "Lapsed",
  statusTone: "warn",
};

const WIN_BACK: ThreadItem[] = [
  {
    kind: "event",
    id: "w1",
    icon: UserMinus,
    title: "Enrolled · Win-back",
    sub: "Lapsed 30+ days",
  },
  {
    kind: "message",
    id: "w2",
    direction: "outgoing",
    body: "Hi Marissa, the studio's not the same without you. Your spot's here whenever you're ready 💛",
  },
  { kind: "system", id: "w3", text: "Waited 3 days · no reply" },
  {
    kind: "email",
    id: "w4",
    direction: "outgoing",
    subject: "Come back for a free week",
    body: "We miss you! Here's a free week to ease back in, no strings attached.",
  },
  {
    kind: "message",
    id: "w5",
    direction: "incoming",
    body: "Aww, I've missed it too. A free week sounds perfect.",
  },
  {
    kind: "message",
    id: "w6",
    direction: "outgoing",
    body: "Yay! Booked you for Monday 6pm. Welcome back!",
  },
  {
    kind: "event",
    id: "w7",
    icon: CalendarCheck,
    title: "Won back",
    sub: "Exited flow · returned",
  },
];

function WinBackScene({ progress, reduce, flow, active }: SceneProps) {
  const visible = useRevealCount(progress, WIN_BACK.length, reduce, active);
  return (
    <Conversation
      contact={MARISSA}
      items={WIN_BACK}
      visible={visible}
      reduce={reduce}
      flow={flow}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                            */
/* ------------------------------------------------------------------ */

const CASES: UseCase[] = [
  {
    id: "new-lead",
    title: "Turn new leads into members",
    description:
      "A booked first class kicks off a welcome, a perfectly timed offer, and a nudge to commit, so leads convert without anyone on your team chasing them.",
    Scene: NewLeadScene,
    steps: NEW_LEAD.length,
  },
  {
    id: "no-show",
    title: "Save the class they almost skipped",
    description:
      "A no-show triggers an instant, friendly text to rebook, recovering the visits, and revenue, that usually slip away unnoticed.",
    Scene: NoShowScene,
    steps: NO_SHOW.length,
  },
  {
    id: "win-back",
    title: "Win members back before they're gone",
    description:
      "When someone goes quiet, a multi-touch win-back across text and email brings them back with the right offer at the right moment.",
    Scene: WinBackScene,
    steps: WIN_BACK.length,
  },
];

export function FlowsUseCases() {
  return (
    <FeatureUseCases
      title={
        <>
          From first class to loyal regular.
          <span className="text-muted-foreground">
            {" "}
            Watch a lead become a member, a no-show rebook, and a quiet regular return, the everyday
            wins that add up to real growth.
          </span>
        </>
      }
      cases={CASES}
    />
  );
}
